import React, { useMemo, useState } from 'react';
import { HTTP_STATUS_CODES, HTTP_METHODS } from '../config/httpReference';

const classes = [
  { id: 'all', label: 'All' },
  { id: '1xx', label: '1xx Informational' },
  { id: '2xx', label: '2xx Success' },
  { id: '3xx', label: '3xx Redirection' },
  { id: '4xx', label: '4xx Client Error' },
  { id: '5xx', label: '5xx Server Error' },
];

const HttpReferencePage = () => {
  const [search, setSearch] = useState('');
  const [selectedClass, setSelectedClass] = useState('all');

  const filteredStatuses = useMemo(
    () =>
      HTTP_STATUS_CODES.filter(s => {
        const matchesClass = selectedClass === 'all' || s.category === selectedClass;
        const haystack = `${s.code} ${s.name} ${s.shortMeaning}`.toLowerCase();
        const matchesSearch = haystack.includes(search.toLowerCase());
        return matchesClass && matchesSearch;
      }),
    [search, selectedClass]
  );

  return (
    <div className="flex flex-col gap-6 p-6">
      <header>
        <h1 className="text-2xl font-semibold text-white">HTTP Reference</h1>
        <p className="text-sm text-slate-400">
          Common HTTP status codes and methods for debugging and API design.
        </p>
      </header>

      <section className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by code, name, or meaning…"
            className="w-full rounded-md border border-slate-600 bg-slate-800 px-3 py-2 text-sm text-white placeholder-slate-500 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 md:w-72"
          />
          <div className="flex flex-wrap gap-2">
            {classes.map(c => (
              <button
                key={c.id}
                onClick={() => setSelectedClass(c.id)}
                className={`rounded-full border px-3 py-1 text-xs font-medium ${
                  selectedClass === c.id
                    ? 'border-blue-500 bg-blue-600 text-white'
                    : 'border-slate-600 bg-slate-800 text-slate-400 hover:border-slate-500 hover:bg-slate-700 hover:text-slate-200'
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {filteredStatuses.map(status => (
            <article
              key={status.code}
              className="rounded-lg border border-slate-700/50 bg-slate-800/50 p-4"
            >
              <div className="flex items-baseline justify-between">
                <div className="flex items-baseline gap-2">
                  <span className="text-lg font-semibold text-white">{status.code}</span>
                  <span className="text-sm font-medium text-slate-300">{status.name}</span>
                </div>
                <span className="rounded-full bg-slate-700 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-300">
                  {status.category}
                </span>
              </div>
              <p className="mt-2 text-xs text-slate-400">{status.shortMeaning}</p>
              <div className="mt-3 space-y-1">
                <p className="text-[11px] font-semibold text-slate-200">Common causes</p>
                <ul className="ml-4 list-disc space-y-0.5 text-[11px] text-slate-400">
                  {status.commonCauses.map((cause, idx) => (
                    <li key={idx}>{cause}</li>
                  ))}
                </ul>
              </div>
              <div className="mt-2 space-y-1">
                <p className="text-[11px] font-semibold text-slate-200">Typical fixes</p>
                <ul className="ml-4 list-disc space-y-0.5 text-[11px] text-slate-400">
                  {status.typicalFixes.map((fix, idx) => (
                    <li key={idx}>{fix}</li>
                  ))}
                </ul>
              </div>
            </article>
          ))}
          {filteredStatuses.length === 0 && (
            <div className="col-span-full rounded-md border-2 border-dashed border-slate-700 p-6 text-center text-sm text-slate-400">
              No status codes match your search.
            </div>
          )}
        </div>
      </section>

      <section>
        <h2 className="mb-2 text-lg font-semibold text-white">HTTP Methods</h2>
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {HTTP_METHODS.map(m => (
            <article
              key={m.method}
              className="rounded-lg border border-slate-700/50 bg-slate-800/50 p-4"
            >
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-white">{m.method}</span>
                <div className="flex gap-1">
                  {m.safe && (
                    <span className="rounded-full bg-green-900/50 px-2 py-0.5 text-[10px] font-semibold text-green-300">
                      Safe
                    </span>
                  )}
                  {m.idempotent && (
                    <span className="rounded-full bg-blue-900/50 px-2 py-0.5 text-[10px] font-semibold text-blue-300">
                      Idempotent
                    </span>
                  )}
                  {m.cacheable && (
                    <span className="rounded-full bg-purple-900/50 px-2 py-0.5 text-[10px] font-semibold text-purple-300">
                      Cacheable
                    </span>
                  )}
                </div>
              </div>
              <p className="mt-2 text-xs text-slate-400">{m.description}</p>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
};

export default HttpReferencePage;
